import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { BusinessSessionScope } from '../auth/BusinessSessionScope';
import { setAdminUnauthorizedHandler } from './api/client';
import * as adminAuthApi from './api/endpoints/auth';
import type { AdminLoginResponse } from './api/schemas';
import { storedAdminSessionFrom } from './sign-in';
import {
  clearAdminSession,
  getAdminSession,
  markAdminPasswordChanged,
  restoreAdminSession,
  setAdminSession,
  type StoredAdminSession,
} from './session-store';

export type AdminSessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type AdminSessionContextValue = {
  status: AdminSessionStatus;
  session: StoredAdminSession | null;
  login: (username: string, password: string) => Promise<AdminLoginResponse>;
  changePassword: (currentPassword: string, newPassword: string, idempotencyKey: string) => Promise<void>;
  logout: () => Promise<void>;
};

export const AdminSessionContext = createContext<AdminSessionContextValue | null>(null);

type AdminSessionProviderProps = {
  children: ReactNode;
  /** Called whenever a request comes back 401, after the local session is cleared. */
  onUnauthorized: () => void;
};

/** Mirrors `SessionContext.tsx`; the admin session never touches the owner session's context or store. */
export function AdminSessionProvider({ children, onUnauthorized }: AdminSessionProviderProps) {
  const [session, setSessionState] = useState<StoredAdminSession | null>(() => getAdminSession());
  const [status, setStatus] = useState<AdminSessionStatus>('loading');

  useEffect(() => {
    const restored = restoreAdminSession();
    setSessionState(restored);
    setStatus(restored ? 'authenticated' : 'unauthenticated');
  }, []);

  useEffect(() => {
    setAdminUnauthorizedHandler(() => {
      setSessionState(null);
      setStatus('unauthenticated');
      onUnauthorized();
    });
    return () => setAdminUnauthorizedHandler(null);
  }, [onUnauthorized]);

  const login = useCallback(async (username: string, password: string) => {
    const result = await adminAuthApi.login(username, password);
    const next = storedAdminSessionFrom(result);
    setAdminSession(next);
    setSessionState(next);
    setStatus('authenticated');
    return result;
  }, []);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string, idempotencyKey: string) => {
      await adminAuthApi.changePassword(currentPassword, newPassword, idempotencyKey);
      const next = markAdminPasswordChanged();
      if (next) setSessionState(next);
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await adminAuthApi.logout();
    } finally {
      clearAdminSession();
      setSessionState(null);
      setStatus('unauthenticated');
    }
  }, []);

  const value = useMemo<AdminSessionContextValue>(
    () => ({ status, session, login, changePassword, logout }),
    [status, session, login, changePassword, logout],
  );

  const identity = session ? `admin:${session.adminId}:${session.expiresAt}` : 'anonymous-admin';
  return <AdminSessionContext.Provider value={value}>
    <BusinessSessionScope identity={identity}>{children}</BusinessSessionScope>
  </AdminSessionContext.Provider>;
}
